/* Salt & Lue — theme.js
   No frameworks, no libraries. Everything degrades to a working
   server-rendered page if JavaScript never arrives. */

(function () {
  'use strict';

  const money = (cents) =>
    (window.SaltAndLue && window.SaltAndLue.moneyFormat
      ? window.SaltAndLue.moneyFormat
      : '${{amount}}'
    ).replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
     .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));

  /* ---------------------------------------------------------
     Scroll reveals + the tideline signature
  --------------------------------------------------------- */
  const reveal = () => {
    const targets = document.querySelectorAll('[data-reveal]:not(.is-inview)');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );
    targets.forEach((el) => io.observe(el));
  };

  /* ---------------------------------------------------------
     Header: compact on scroll
  --------------------------------------------------------- */
  const header = () => {
    const el = document.querySelector('[data-header]');
    if (!el) return;
    let ticking = false;
    const update = () => {
      el.classList.toggle('is-compact', window.scrollY > 40);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  /* ---------------------------------------------------------
     Announcement bar rotator
  --------------------------------------------------------- */
  const announcements = () => {
    const bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    const items = bar.querySelectorAll('.announcement__item');
    if (items.length < 2) return;
    const gap = parseInt(bar.dataset.interval, 10) || 5000;
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, gap);
  };

  /* ---------------------------------------------------------
     Drawers — menu, search, cart
  --------------------------------------------------------- */
  let lastFocused = null;

  const openDrawer = (id) => {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const focusable = drawer.querySelector(
      'input, button, a[href], select, textarea'
    );
    if (focusable) window.setTimeout(() => focusable.focus(), 120);
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open')) {
      document.body.classList.remove('is-locked');
    }
    if (lastFocused) lastFocused.focus();
  };

  const drawers = () => {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        openDrawer(opener.dataset.drawerOpen);
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeDrawer(closer.closest('.drawer'));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      closeDrawer(document.querySelector('.drawer.is-open'));
    });

    // Keep focus inside an open drawer
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const drawer = document.querySelector('.drawer.is-open');
      if (!drawer) return;
      const items = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  };

  /* ---------------------------------------------------------
     Cart
  --------------------------------------------------------- */
  const refreshCart = async ({ open } = {}) => {
    try {
      const res = await fetch(
        window.Shopify.routes.root + '?section_id=cart-drawer'
      );
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-contents]');
      const current = document.querySelector('[data-cart-contents]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
      if (open) openDrawer('cart-drawer');
    } catch (err) {
      // If anything goes wrong, fall back to the real cart page.
      window.location.href = window.Shopify.routes.root + 'cart';
    }
  };

  const setBusy = (btn, busy, restore) => {
    if (!btn) return;
    btn.disabled = busy;
    if (busy) {
      btn.dataset.label = btn.textContent;
      btn.textContent = btn.dataset.busyLabel || 'Adding…';
    } else {
      btn.textContent = restore || btn.dataset.label || btn.textContent;
    }
  };

  const cart = () => {
    // Add to bag (product form + quick add)
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-add-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      setBusy(btn, true);
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        setBusy(btn, false, 'Added');
        window.setTimeout(() => setBusy(btn, false), 1600);
        await refreshCart({ open: true });
      } catch (err) {
        setBusy(btn, false);
        const msg = form.querySelector('[data-form-message]');
        if (msg) {
          msg.textContent = err.message || 'That one is unavailable right now.';
          msg.className = 'form-message form-message--error';
        }
      }
    });

    // Quantity + remove inside the drawer
    document.addEventListener('click', async (e) => {
      const control = e.target.closest('[data-cart-change]');
      if (!control) return;
      e.preventDefault();
      const line = control.dataset.line;
      const quantity = control.dataset.cartChange;
      const wrapper = control.closest('[data-cart-contents]');
      if (wrapper) wrapper.style.opacity = '0.5';
      await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      await refreshCart();
      const after = document.querySelector('[data-cart-contents]');
      if (after) after.style.opacity = '1';
    });
  };

  /* ---------------------------------------------------------
     Product page: variants, gallery, sticky buy
  --------------------------------------------------------- */
  const productForm = () => {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const data = root.querySelector('[data-variant-json]');
      if (!data) return;
      const variants = JSON.parse(data.textContent);
      const idInput = root.querySelector('[data-variant-id]');
      const submit = root.querySelector('[data-add-button]');
      const priceEl = root.querySelector('[data-price]');
      const stockEl = root.querySelector('[data-stock]');

      const selected = () =>
        Array.from(root.querySelectorAll('[data-option-index]')).reduce(
          (acc, input) => {
            if (input.checked) acc[Number(input.dataset.optionIndex)] = input.value;
            return acc;
          },
          []
        );

      const update = () => {
        const chosen = selected();
        const match = variants.find((v) =>
          v.options.every((opt, i) => opt === chosen[i])
        );

        root.querySelectorAll('[data-option-value]').forEach((label) => {
          const input = label.querySelector('input');
          if (!input) return;
          const idx = Number(input.dataset.optionIndex);
          const probe = chosen.slice();
          probe[idx] = input.value;
          const possible = variants.some(
            (v) =>
              v.available &&
              v.options.every((opt, i) => (i === idx ? opt === input.value : opt === probe[i]))
          );
          label.classList.toggle('is-unavailable', !possible);
        });

        const valueLabels = root.querySelectorAll('[data-option-current]');
        valueLabels.forEach((el) => {
          const idx = Number(el.dataset.optionCurrent);
          el.textContent = chosen[idx] || '';
        });

        if (!match) {
          if (submit) {
            submit.disabled = true;
            submit.textContent = 'Unavailable';
          }
          return;
        }

        if (idInput) idInput.value = match.id;
        if (submit) {
          submit.disabled = !match.available;
          submit.textContent = match.available ? 'Add to bag' : 'Sold out';
        }
        if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;

        if (stockEl) {
          const low = Number(stockEl.dataset.threshold || 5);
          if (match.available && match.inventory_management && match.inventory_quantity > 0 && match.inventory_quantity <= low) {
            stockEl.textContent = `Only ${match.inventory_quantity} left`;
            stockEl.hidden = false;
          } else {
            stockEl.hidden = true;
          }
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);

        // Jump the gallery to the variant image
        if (match.featured_media_id) {
          const slide = root.querySelector(
            `[data-media-id="${match.featured_media_id}"]`
          );
          if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      };

      root.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-index]')) update();
      });
      update();

      // Quantity stepper
      root.querySelectorAll('[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = root.querySelector('[data-qty-input]');
          if (!input) return;
          const next = Math.max(1, Number(input.value) + Number(btn.dataset.qtyStep));
          input.value = next;
          const label = root.querySelector('[data-qty-label]');
          if (label) label.textContent = next;
        });
      });
    });

    // Sticky add to bag appears once the real button scrolls away
    const sticky = document.querySelector('[data-sticky-buy]');
    const anchor = document.querySelector('[data-add-button]');
    if (sticky && anchor && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) =>
            sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0)
          );
        },
        { threshold: 0 }
      );
      io.observe(anchor);
    }
  };

  /* ---------------------------------------------------------
     Collection: auto-submit sort and filters
  --------------------------------------------------------- */
  const collection = () => {
    const form = document.querySelector('[data-filter-form]');
    if (!form) return;
    form.addEventListener('change', () => form.submit());
    const sort = document.querySelector('[data-sort]');
    if (sort) {
      sort.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }
  };

  /* --------------------------------------------------------- */
  const init = () => {
    window.Shopify = window.Shopify || {};
    window.Shopify.routes = window.Shopify.routes || { root: '/' };
    reveal();
    header();
    announcements();
    drawers();
    cart();
    productForm();
    collection();
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Sections re-render in the theme editor; re-run the pieces that need it.
  document.addEventListener('shopify:section:load', () => {
    reveal();
    announcements();
    productForm();
  });

  window.SaltAndLue = Object.assign(window.SaltAndLue || {}, { refreshCart, money });
})();
